/* ============================================================
   GANO KEDAH — shared interaction primitives
   ============================================================
   Exposed primitives (auto-run on DOMContentLoaded):

   1. Scroll reveal: any `.gk-reveal` element fades + rises when it
      enters the viewport. Stagger via inline `--gk-reveal-delay`.

   2. Counter-up: `[data-gk-counter]` elements count from 0 to their
      final integer on first viewport entry. Works with suffixes.
      Example: <span data-gk-counter="130">0</span>M
      Respects prefers-reduced-motion (skips animation, renders final).

   3. Pointer-spotlight: any `.gk-spotlight` element gets a CSS
      custom property `--gk-px` / `--gk-py` set on mousemove — CSS
      can use `radial-gradient(circle at var(--gk-px) var(--gk-py), ...)`.

   4. Tilt: any `[data-gk-tilt]` element gets a subtle 3D tilt on
      mouse move. Disables on touch devices. Magnitude capped at 6deg.

   Everything is idempotent — safe to call init() again if Shopify
   theme editor re-renders sections.
   ============================================================ */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = "ontouchstart" in window;

  // ---- 1. Reveal on view ----
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll(".gk-reveal:not(.gk-reveal-bound)");
    if (!nodes.length) return;

    if (reduced) {
      nodes.forEach(function (n) { n.classList.add("gk-visible", "gk-reveal-bound"); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("gk-visible");
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.15 });

    nodes.forEach(function (n) {
      n.classList.add("gk-reveal-bound");
      io.observe(n);
    });
  }

  // ---- 2. Counter-up ----
  function initCounters(root) {
    var nodes = (root || document).querySelectorAll("[data-gk-counter]:not(.gk-counter-bound)");
    if (!nodes.length) return;

    nodes.forEach(function (n) {
      n.classList.add("gk-counter-bound");
      var target = parseFloat(n.getAttribute("data-gk-counter"));
      if (isNaN(target)) return;

      if (reduced) {
        n.textContent = formatNumber(target);
        return;
      }

      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          obs.unobserve(e.target);
          animateCounter(e.target, target);
        });
      }, { threshold: 0.3 });
      io.observe(n);
    });
  }

  function animateCounter(el, target) {
    var duration = 1800;
    var start = performance.now();
    var startVal = 0;
    function tick(now) {
      var t = Math.min(1, (now - start) / duration);
      // easeOutExpo
      var eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      var v = startVal + (target - startVal) * eased;
      el.textContent = formatNumber(v, target);
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = formatNumber(target);
    }
    requestAnimationFrame(tick);
  }

  function formatNumber(n, target) {
    var int = Math.floor(n);
    // European dot-grouped format for large numbers (e.g. 130.000.000).
    // Check both target (during animation) and final int (final frame call).
    var threshold = target || int;
    if (threshold >= 10000) {
      return int.toLocaleString("de-DE");
    }
    return String(int);
  }

  // ---- 3. Pointer spotlight ----
  function initSpotlight(root) {
    var nodes = (root || document).querySelectorAll(".gk-spotlight:not(.gk-spot-bound)");
    nodes.forEach(function (n) {
      n.classList.add("gk-spot-bound");
      if (isTouch) return;
      n.addEventListener("mousemove", function (e) {
        var r = n.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        n.style.setProperty("--gk-px", x + "%");
        n.style.setProperty("--gk-py", y + "%");
      });
    });
  }

  // ---- 4. 3D tilt ----
  function initTilt(root) {
    var nodes = (root || document).querySelectorAll("[data-gk-tilt]:not(.gk-tilt-bound)");
    nodes.forEach(function (n) {
      n.classList.add("gk-tilt-bound");
      if (isTouch || reduced) return;

      var max = parseFloat(n.getAttribute("data-gk-tilt")) || 6;
      var rect = null;
      var raf = null;

      function onMove(e) {
        if (!rect) rect = n.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        var rx = (0.5 - py) * max;
        var ry = (px - 0.5) * max;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          n.style.transform =
            "perspective(900px) rotateX(" + rx.toFixed(2) + "deg) rotateY(" + ry.toFixed(2) + "deg) translateZ(0)";
        });
      }
      function onLeave() {
        rect = null;
        if (raf) cancelAnimationFrame(raf);
        n.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
      }
      function onEnter() { rect = n.getBoundingClientRect(); }

      n.addEventListener("mouseenter", onEnter);
      n.addEventListener("mousemove", onMove);
      n.addEventListener("mouseleave", onLeave);
    });
  }

  // ---- 5. Pause offscreen: toggles .gk-paused on .gk-section when it
  //        leaves the viewport. Continuous CSS animations tagged with
  //        .gk-anim-bg (background decorations) or .gk-anim-loop
  //        (marquees, etc.) are paused via the .gk-paused scope so the
  //        GPU isn't painting blurred gradients for an offscreen
  //        element. Major perf win for pages with many sections. ---
  function initPauseOffscreen(root) {
    var nodes = (root || document).querySelectorAll(".gk-section:not(.gk-perf-bound)");
    if (!nodes.length) return;

    if (!("IntersectionObserver" in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) e.target.classList.remove("gk-paused");
        else e.target.classList.add("gk-paused");
      });
    }, { rootMargin: "120px" });

    nodes.forEach(function (s) {
      s.classList.add("gk-perf-bound");
      io.observe(s);
    });
  }

  // ---- Init everything ----
  function initAll(root) {
    initReveal(root);
    initCounters(root);
    initSpotlight(root);
    initTilt(root);
    initPauseOffscreen(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor hooks — re-init when a section is re-rendered
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });
  document.addEventListener("shopify:section:select", function (e) { initAll(e.target); });

  // Expose for section-scoped scripts to call
  window.GanoKedah = {
    initAll: initAll,
    initReveal: initReveal,
    initCounters: initCounters,
    initSpotlight: initSpotlight,
    initTilt: initTilt,
    initPauseOffscreen: initPauseOffscreen
  };
})();
